/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AccountResponse } from '../models/AccountResponse';
import type { EnrolConfirmRequest } from '../models/EnrolConfirmRequest';
import type { EnrolConfirmResponse } from '../models/EnrolConfirmResponse';
import type { EnrolRequest } from '../models/EnrolRequest';
import type { EnrolResponse } from '../models/EnrolResponse';
import type { jmt_api__routes__auth__SimpleResponse } from '../models/jmt_api__routes__auth__SimpleResponse';
import type { LoginRequest } from '../models/LoginRequest';
import type { LoginResponse } from '../models/LoginResponse';
import type { LogoutRequest } from '../models/LogoutRequest';
import type { MfaRequest } from '../models/MfaRequest';
import type { PasswordChangeRequest } from '../models/PasswordChangeRequest';
import type { SessionListResponse } from '../models/SessionListResponse';
import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';
export class AuthService {
    constructor(public readonly httpRequest: BaseHttpRequest) {}
    /**
     * Login
     * Verify the password. Never returns a session — that is what `/auth/mfa` is for.
     *
     * Four things happen here that are easy to leave out and expensive to add later:
     *
     * 1. **The throttle is checked before the hash is computed**, so a refused attempt costs
     * nothing. Checking afterwards would make the delay a formality that still spends 90 ms
     * of CPU per attempt.
     * 2. **An unknown email still pays for a verification** (`verify_dummy`), so response time
     * does not answer "does this address have an account here?".
     * 3. **A hash made under weaker parameters is upgraded**, here and nowhere else, because
     * this is the one moment a plaintext password is available to rehash with.
     * 4. **An account with no authenticator gets an `enrol` challenge, not a session.** MFA is
     * required rather than optional, and first run has to be able to enrol without ever
     * holding a session that skipped it.
     * @returns LoginResponse Successful Response
     * @throws ApiError
     */
    public login({
        requestBody,
    }: {
        requestBody: LoginRequest,
    }): CancelablePromise<LoginResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/v1/auth/login',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Logout
     * Revoke the session row, then clear the cookies.
     *
     * In that order, and the row is the part that matters: deleting the cookie alone would leave
     * a token that still authenticates anywhere it had been copied, which is the whole reason
     * sessions are rows rather than JWTs.
     * @returns jmt_api__routes__auth__SimpleResponse Successful Response
     * @throws ApiError
     */
    public logout({
        requestBody,
    }: {
        requestBody: LogoutRequest,
    }): CancelablePromise<jmt_api__routes__auth__SimpleResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/v1/auth/logout',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Me
     * @returns AccountResponse Successful Response
     * @throws ApiError
     */
    public me(): CancelablePromise<AccountResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/auth/me',
        });
    }
    /**
     * Complete Mfa
     * Complete the login with a TOTP code or a recovery code, and set the session cookie.
     * @returns AccountResponse Successful Response
     * @throws ApiError
     */
    public completeMfa({
        requestBody,
    }: {
        requestBody: MfaRequest,
    }): CancelablePromise<AccountResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/v1/auth/mfa',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Enrol Mfa
     * Begin enrolment: generate a seed, return it once, store nothing yet.
     *
     * Two ways in, and both are authenticated:
     *
     * - **First enrolment** — the `enrol` challenge from `/auth/login`, so the password has just
     * been proven. There is no session to require, because a session is exactly what cannot be
     * issued before MFA exists.
     * - **Device change** — a valid session *plus* a current code, the step-up `AuthN-AuthZ`
     * requires for this action. A session alone would mean an unlocked laptop is enough to move
     * the second factor onto a new phone, which would make the second factor decorative.
     * @returns EnrolResponse Successful Response
     * @throws ApiError
     */
    public enrolMfa({
        requestBody,
    }: {
        requestBody: EnrolRequest,
    }): CancelablePromise<EnrolResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/v1/auth/mfa/enroll',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Confirm Enrolment
     * Prove the new authenticator works, then store the seed, enable MFA and issue codes.
     *
     * The order is the point. The code is verified against the *pending* seed carried by the
     * token before anything is written, so a failed confirmation leaves the account exactly as it
     * was — old authenticator still working, or still unenrolled.
     *
     * A session is issued when the caller has none, because by this point both factors have been
     * proven: the token is bound to the password, and the code proves the device. Requiring a
     * separate login immediately afterwards would add a step that proves nothing new.
     * @returns EnrolConfirmResponse Successful Response
     * @throws ApiError
     */
    public confirmEnrolment({
        requestBody,
    }: {
        requestBody: EnrolConfirmRequest,
    }): CancelablePromise<EnrolConfirmResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/v1/auth/mfa/enroll/confirm',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Change Password
     * Change the password: current password, plus a step-up code, then revoke other sessions.
     *
     * All three, per `AuthN-AuthZ`: the current password proves this is not an unattended
     * keyboard, the code is the step-up this action is listed for, and revoking every *other*
     * session is what makes a password change useful after a suspected compromise. The caller's
     * own session survives, so changing a password does not log you out of the window you
     * changed it in.
     * @returns jmt_api__routes__auth__SimpleResponse Successful Response
     * @throws ApiError
     */
    public changePassword({
        requestBody,
    }: {
        requestBody: PasswordChangeRequest,
    }): CancelablePromise<jmt_api__routes__auth__SimpleResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/v1/auth/password',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * List Sessions
     * @returns SessionListResponse Successful Response
     * @throws ApiError
     */
    public listSessions(): CancelablePromise<SessionListResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/auth/sessions',
        });
    }
    /**
     * Revoke Session
     * Revoke one session. 404 when it is not this account's — never 403.
     *
     * `AuthN-AuthZ#enforcement` is explicit: 404 rather than 403 avoids confirming that the
     * object exists. The ownership predicate lives in the repository function, so this handler
     * cannot forget it.
     * @returns jmt_api__routes__auth__SimpleResponse Successful Response
     * @throws ApiError
     */
    public revokeSession({
        sessionId,
    }: {
        sessionId: string,
    }): CancelablePromise<jmt_api__routes__auth__SimpleResponse> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/api/v1/auth/sessions/{session_id}',
            path: {
                'session_id': sessionId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
}
